import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { BarChart3, Filter, Download, ListChecks, Building2, FlaskConical, Eye, AlertTriangle, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { StaggeredCategoryTick } from "@/components/charts/StaggeredCategoryTick";

const TABS = ["overview", "questions", "clubs", "contacts"];
// Institutional Observer is read-only on aggregated public stats only —
// they cannot reach `/admin/stats/questions/*`, `/admin/stats/clubs`, or
// `/admin/export/contacts.csv` (all 403 by design). Restrict their tab set
// to the overview pane so the UI never triggers a forbidden sub-fetch.
const OBSERVER_TABS = ["overview"];

const AdminStatsAdvanced = () => {
  const { user: me } = useAuth();
  const { lang, t, pickI18n, tProfile, tCountry, tQuestionnaireTitle } = useLang();
  const isManager = me?.role === "qa_manager";
  const isObserver = me?.role === "institutional_observer";
  const visibleTabs = isObserver ? OBSERVER_TABS : TABS;

  // Local resolvers that DEFER to the centralized helpers but still
  // bridge in extra context (the profile / country lookup docs from
  // `/api/profiles` and `/api/countries`). Chart axes stay stable: the
  // resolvers never mutate the underlying key (profile_key, ISO code).
  const tProfileLocal = (key) => tProfile(key, profiles.find((p) => p.key === key));
  const tStatus = (key) => {
    const r = t(`leadStatuses.${key}`);
    return r && r !== `leadStatuses.${key}` ? r : key;
  };
  const tLang = (code) => {
    const map = { en: "EN", es: "ES", zh: "中文", "pt-br": "PT-BR" };
    return map[code] || (code || "—").toUpperCase();
  };
  const tCountryLocal = (code) => tCountry(code, countries.find((c) => c.code === code));

  const [tab, setTab] = useState("overview");
  const [profiles, setProfiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [filters, setFilters] = useState({
    profile_key: "",
    country: "",
    language: "",
    consent_status: "",
    has_consent: "",
    completed_questionnaire_id: "",
    not_completed_questionnaire_id: "",
    test_mode: "public",
  });
  const [overview, setOverview] = useState(null);
  const [questionStats, setQuestionStats] = useState(null);
  const [clubStats, setClubStats] = useState(null);
  const [pickedQid, setPickedQid] = useState("");
  const [contactBasis, setContactBasis] = useState("crm");
  const [loading, setLoading] = useState(false);
  // PREVIEW-ADMIN-STATISTICS-403-GRACEFUL-HANDLING-1 (Feb 2026) — when
  // the user's role is not allowed to read the stats endpoints (e.g. a
  // QA Racket Technician staff impersonated by DemoPreview) every axios
  // call to `/admin/stats/*` answers 403. We catch the first 403 and
  // render a controlled forbidden state instead of letting the unhandled
  // promise rejection surface as a red runtime overlay.
  const [forbidden, setForbidden] = useState(false);

  // Boot lookups
  useEffect(() => {
    // Defensive catches — without them, a 403 on any of these (e.g.
    // staff QA without admin scope) bubbles up as an unhandled promise
    // rejection and triggers the React error overlay.
    api.get("/profiles")
      .then((r) => setProfiles(r.data || []))
      .catch(() => setProfiles([]));
    api.get("/countries")
      .then((r) => setCountries(r.data || []))
      .catch(() => setCountries([]));
    // Observers are blocked from `/admin/questionnaires` (403 by design).
    // Skip the fetch entirely so we never surface a forbidden error toast.
    if (!isObserver) {
      api.get("/admin/questionnaires")
        .then((r) => {
          const items = (r.data || []).filter((q) => q.is_active && !q.is_internal);
          setQuestionnaires(items);
          if (items[0]) setPickedQid(items[0].id);
        })
        .catch((e) => {
          // A 403 here means the current role is not authorized to
          // browse questionnaires. Mark the whole stats surface as
          // forbidden so the page renders a controlled empty state.
          if (e?.response?.status === 403) setForbidden(true);
        });
    }
  }, [isObserver]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p.set(k, v);
    });
    return p.toString();
  }, [filters]);

  // Country list shown alphabetically by the user's localized name. We
  // depend on `lang` so switching language re-sorts on the fly. ISO codes
  // are unaffected — only the visible label drives the order.
  const sortedCountries = useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry(a.code, a);
      const bn = tCountry(b.code, b);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);

  // ISO-code → localized country name. Used by chart axis labels so the
  // X-axis renders "España / Spain / 西班牙" instead of the raw "ES" code.
  const countryByCode = useMemo(() => {
    const map = {};
    for (const c of countries) map[c.code] = c;
    return map;
  }, [countries]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/stats/overview?${queryString}`);
      setOverview(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(extractErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    if (!pickedQid) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ test_mode: filters.test_mode });
      if (filters.profile_key) p.set("profile_key", filters.profile_key);
      if (filters.country) p.set("country", filters.country);
      const { data } = await api.get(`/admin/stats/questions/${pickedQid}?${p.toString()}`);
      setQuestionStats(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(extractErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/stats/clubs?test_mode=${filters.test_mode}`);
      setClubStats(data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(extractErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (forbidden) return;
    if (tab === "overview") fetchOverview();
    if (tab === "questions") fetchQuestions();
    if (tab === "clubs") fetchClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, queryString, pickedQid, forbidden]);

  const downloadContacts = async () => {
    const p = new URLSearchParams({ test_mode: filters.test_mode, consent_basis: contactBasis });
    if (filters.profile_key) p.set("profile_key", filters.profile_key);
    if (filters.country) p.set("country", filters.country);
    if (filters.language) p.set("language", filters.language);
    if (filters.completed_questionnaire_id) p.set("completed_questionnaire_id", filters.completed_questionnaire_id);
    if (filters.not_completed_questionnaire_id) p.set("not_completed_questionnaire_id", filters.not_completed_questionnaire_id);
    try {
      const res = await api.get(`/admin/export/contacts.csv?${p.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "teamsportworld_contacts.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  const reset = () => {
    // Reset every Advanced Stats filter to its default and re-anchor the
    // view at the Overview tab. We always call setFilters even when the
    // filters were already default — useful so the operation is
    // observable to the user (toast + rerender).
    setFilters({
      profile_key: "", country: "", language: "", consent_status: "", has_consent: "",
      completed_questionnaire_id: "", not_completed_questionnaire_id: "", test_mode: "public",
    });
    setTab("overview");
    setQuestionStats(null);
    setClubStats(null);
    setContactBasis("crm");
    if (questionnaires[0]) setPickedQid(questionnaires[0].id);
    toast.success(t("statsAdv.reset") + " ✓");
  };

  const doPrint = () => {
    // window.print() is blocked when the SPA is iframed (the Emergent
    // preview wraps the app inside its own UI). Snapshot the currently
    // active stats pane into a fresh top-level window — that one isn't
    // sandboxed and triggers the OS print dialog reliably.
    const pane = document.querySelector(`[data-testid="stats-${tab}-pane"]`);
    const headerEl = document.querySelector('[data-testid="admin-stats-advanced"] > div:first-child');
    const filtersBar = document.querySelector('[data-testid="stats-filter-bar"]');
    if (!pane) {
      toast.error(t("statsAdv.print") + " — " + t("statsAdv.printNoView"));
      return;
    }
    const w = window.open("", "_blank", "width=1100,height=900");
    if (!w) {
      toast.error(t("phase2.printBlocked"));
      return;
    }
    const styles = Array.from(document.querySelectorAll("link[rel=stylesheet], style"))
      .map((n) => n.outerHTML)
      .join("\n");
    const title = `${t("statsAdv.title")} — TEAMSPORTWORLD`;
    w.document.open();
    w.document.write(`<!doctype html><html lang="${lang || "en"}"><head><meta charset="utf-8"><title>${title}</title>${styles}<style>
      body { background:#fff; padding:24px; font-family:Inter, system-ui, sans-serif; color:#0f172a; }
      .print-meta { color:#475569; font-size:12px; margin-bottom:12px; }
      .tsw-card { box-shadow:none; border:1px solid #cbd5e1; page-break-inside:avoid; break-inside:avoid; margin-bottom:14px; }
      table { page-break-inside:auto; }
      tr { page-break-inside:avoid; break-inside:avoid; }
      img, svg { break-inside:avoid; page-break-inside:avoid; }
      /* Page geometry — comfortable top margin so page 2+ doesn't bleed
         into the very edge. Page 1 keeps a slightly tighter top margin so
         the existing header layout is unaffected. */
      @page { margin: 18mm 14mm 16mm 14mm; }
      @page :first { margin-top: 10mm; }
      @media print {
        body { padding:0; }
        /* Encourage the chart cards to flow to a new page rather than be
           split across two pages — keeps each chart visually intact. */
        .tsw-card { page-break-inside: avoid; break-inside: avoid; }
      }
    </style></head><body>
      <div class="print-meta"><strong>${title}</strong> · ${new Date().toLocaleString()} · ${t(`statsAdv.${tab}`) || tab}</div>
      ${headerEl ? headerEl.outerHTML : ""}
      ${filtersBar ? filtersBar.outerHTML : ""}
      ${pane.outerHTML}
    </body></html>`);
    w.document.close();
    // Charts (recharts) render into <svg>; cloned outerHTML preserves them.
    setTimeout(() => {
      try { w.focus(); w.print(); } catch (_) { /* noop */ }
    }, 350);
  };

  return (
    <div className="space-y-6" data-testid="admin-stats-advanced">
      <div>
        <div className="tsw-overline">{t("admin.title")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-900">
          <BarChart3 className="inline-block w-7 h-7 -mt-1 me-2 text-emerald-600" />
          {t("statsAdv.title")}
        </h1>
        <p className="text-slate-600 mt-2 max-w-3xl">{t("statsAdv.sub")}</p>
        {isManager && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-800" data-testid="stats-manager-banner">
            <AlertTriangle className="w-4 h-4" /> {t("statsAdv.scopedBanner")}
          </div>
        )}
      </div>

      {/* Controlled forbidden state — replaces the rest of the surface
          when the current role isn't allowed to read the stats. Avoids
          the React runtime error overlay that used to surface when
          axios 403s bubbled up unhandled. */}
      {forbidden && (
        <div
          className="tsw-card p-8 text-center border border-amber-200 bg-amber-50/40"
          data-testid="admin-stats-forbidden-state"
        >
          <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">
            {t("statsAdv.forbiddenTitle")}
          </h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto">
            {t("statsAdv.forbiddenBody")}
          </p>
        </div>
      )}

      {!forbidden && (
      <>
      {/* Filter bar */}
      <div className="tsw-card p-4 space-y-3" data-testid="stats-filter-bar">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Filter className="w-4 h-4 text-emerald-600" /> {t("statsAdv.filters")}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="tsw-label">{t("statsAdv.profile")}</label>
            <select className="tsw-input" value={filters.profile_key} onChange={(e) => setFilters((f) => ({ ...f, profile_key: e.target.value }))} data-testid="stats-filter-profile">
              <option value="">{t("statsAdv.allProfiles")}</option>
              {profiles.map((p) => <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>)}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("statsAdv.country")}</label>
            <select className="tsw-input" value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))} data-testid="stats-filter-country">
              <option value="">{t("statsAdv.allCountries")}</option>
              {sortedCountries.map((c) => <option key={c.code} value={c.code}>{`${tCountry(c.code, c)} (${c.code})`}</option>)}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("statsAdv.language")}</label>
            <select className="tsw-input" value={filters.language} onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))} data-testid="stats-filter-language">
              <option value="">{t("statsAdv.none")}</option>
              {LANGUAGES.filter((Lg) => Lg.isActive).map((Lg) => (
                <option key={Lg.code} value={Lg.code}>{`${Lg.code.toUpperCase()} — ${Lg.nameNative}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("statsAdv.testMode")}</label>
            <select className="tsw-input" value={filters.test_mode} onChange={(e) => setFilters((f) => ({ ...f, test_mode: e.target.value }))} data-testid="stats-filter-testmode">
              <option value="public">{t("statsAdv.publicOnly")}</option>
              <option value="internal">{t("statsAdv.internalOnly")}</option>
              <option value="all">{t("statsAdv.all")}</option>
            </select>
          </div>
          {!isObserver && (
            <div>
              <label className="tsw-label">{t("statsAdv.consent")}</label>
              <select className="tsw-input" value={filters.consent_status} onChange={(e) => setFilters((f) => ({ ...f, consent_status: e.target.value }))} data-testid="stats-filter-consent">
                <option value="">{t("statsAdv.none")}</option>
                <option value="research">{t("statsAdv.consentResearch")}</option>
                <option value="crm">{t("statsAdv.consentCrmShort")}</option>
                <option value="commercial">{t("statsAdv.consentCommercialShort")}</option>
                <option value="pilot">{t("statsAdv.consentPilot")}</option>
              </select>
            </div>
          )}
          {!isObserver && (
            <div>
              <label className="tsw-label">{t("statsAdv.completed")}</label>
              <select className="tsw-input" value={filters.completed_questionnaire_id} onChange={(e) => setFilters((f) => ({ ...f, completed_questionnaire_id: e.target.value }))} data-testid="stats-filter-completed">
                <option value="">{t("statsAdv.none")}</option>
                {questionnaires.map((q) => <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>)}
              </select>
            </div>
          )}
          {!isObserver && (
            <div>
              <label className="tsw-label">{t("statsAdv.notCompleted")}</label>
              <select className="tsw-input" value={filters.not_completed_questionnaire_id} onChange={(e) => setFilters((f) => ({ ...f, not_completed_questionnaire_id: e.target.value }))} data-testid="stats-filter-notcompleted">
                <option value="">{t("statsAdv.none")}</option>
                {questionnaires.map((q) => <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>)}
              </select>
            </div>
          )}
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2 justify-end">
            <button
              type="button"
              onClick={doPrint}
              className="tsw-btn-secondary"
              data-testid="stats-print"
            >
              <Printer className="w-4 h-4" /> {t("statsAdv.print")}
            </button>
            <button
              type="button"
              onClick={reset}
              className="tsw-btn-secondary"
              data-testid="stats-filter-reset"
            >
              <RotateCcw className="w-4 h-4" /> {t("statsAdv.reset")}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {visibleTabs.map((tk) => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === tk ? "text-emerald-700 border-emerald-600" : "text-slate-600 border-transparent hover:text-slate-900"}`}
            data-testid={`stats-tab-${tk}`}
          >
            {tk === "overview" && <Eye className="inline w-4 h-4 me-1" />}
            {tk === "questions" && <ListChecks className="inline w-4 h-4 me-1" />}
            {tk === "clubs" && <Building2 className="inline w-4 h-4 me-1" />}
            {tk === "contacts" && <Download className="inline w-4 h-4 me-1" />}
            {t(`statsAdv.${tk}`)}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-500 text-sm">…</div>}

      {tab === "overview" && overview && (
        <div className="space-y-4" data-testid="stats-overview-pane">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Stat id="users" label={t("statsAdv.statUsers")} value={overview.total_users} />
            <Stat id="verified" label={t("statsAdv.statVerified")} value={overview.verified_users} />
            <Stat id="pilot-ready" label={t("statsAdv.statPilotReady")} value={overview.pilot_ready} />
            <Stat id="research" label={t("statsAdv.statResearch")} value={overview.research_consent} />
            <Stat id="crm" label={t("statsAdv.statCrm")} value={overview.crm_contact_consent} />
            <Stat id="commercial" label={t("statsAdv.statCommercial")} value={overview.commercial_consent} />
            <Stat id="responses" label={t("statsAdv.statResponses")} value={overview.total_responses} />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title={t("statsAdv.profile")} lang={lang} data={overview.by_profile.map((b) => ({ name: tProfileLocal(b.key), key: b.key, count: b.count }))} color="#059669" />
            <ChartCard title={t("statsAdv.country")} lang={lang} data={overview.by_country.map((b) => ({ name: tCountryLocal(b.country), code: b.country, count: b.count }))} color="#F97316" />
            <ChartCard title={t("statsAdv.language")} lang={lang} data={overview.by_language.map((b) => ({ name: tLang(b.language), code: b.language, count: b.count }))} color="#0EA5E9" />
            <ChartCard title={t("statsAdv.byQuestionnaire")} lang={lang} data={overview.by_questionnaire.map((b) => ({ name: tQuestionnaireTitle(b).slice(0, 28), id: b.id, count: b.count }))} color="#EC4899" vertical />
          </div>
        </div>
      )}

      {tab === "questions" && (
        <div className="space-y-4" data-testid="stats-questions-pane">
          <div className="tsw-card p-4">
            <label className="tsw-label">{t("statsAdv.pickQ")}</label>
            <select className="tsw-input" value={pickedQid} onChange={(e) => setPickedQid(e.target.value)} data-testid="stats-q-picker">
              {questionnaires.map((q) => <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>)}
            </select>
          </div>
          {questionStats && (
            <div className="space-y-4">
              <div className="text-sm text-slate-700">
                <strong>{pickI18n(questionStats, "title") || questionStats.title_en}</strong> · {questionStats.respondents_total} {t("statsAdv.respondents")}
              </div>
              {questionStats.questions.map((q) => (
                <div key={q.id} className="tsw-card p-4" data-testid={`stats-q-${q.code || q.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-mono text-emerald-700">{q.code}</div>
                      <div className="font-medium text-slate-900">{pickI18n(q, "label") || q.label_en}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {q.answered_count} {t("statsAdv.answered")} {t("statsAdv.of")} {q.respondents_total}
                        {q.type === "scale" && q.mean !== undefined && (
                          <span className="ms-2 text-slate-700">· {t("statsAdv.mean")} {q.mean} · {t("statsAdv.min")} {q.min} · {t("statsAdv.max")} {q.max}</span>
                        )}
                      </div>
                    </div>
                    {q.is_sensitive && (
                      <span className="tsw-chip bg-rose-50 text-rose-700 border border-rose-200" data-testid={`stats-q-sensitive-${q.code}`}>
                        <FlaskConical className="inline w-3 h-3 me-1" /> {t("statsAdv.sensitiveBadge")}
                      </span>
                    )}
                  </div>
                  {q.distribution?.length > 0 ? (
                    <div className="mt-3 space-y-1.5">
                      {q.distribution.map((d, i) => (
                        <div key={`${d.value}-${i}`} className="text-xs">
                          <div className="flex justify-between mb-0.5 text-slate-600">
                            <span className="truncate pe-2">{pickI18n(d, "label") || d.label_en || d.value}</span>
                            <span className="font-mono whitespace-nowrap">{d.count} · {d.pct}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, d.pct || 0)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-400 italic">
                      {q.type === "text" || q.type === "textarea"
                        ? `${t("statsAdv.freeText")} (${q.free_text_count || 0})`
                        : "—"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "clubs" && clubStats && (
        <div className="tsw-card p-4 space-y-2" data-testid="stats-clubs-pane">
          <div className="text-sm text-slate-700"><strong>{clubStats.count}</strong> {t("statsAdv.clubs")}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-start">{t("statsAdv.colName")}</th>
                  <th className="px-3 py-2 text-start">{t("statsAdv.country")}</th>
                  <th className="px-3 py-2 text-start">{t("statsAdv.language")}</th>
                  <th className="px-3 py-2 text-start">{t("statsAdv.colCreated")}</th>
                </tr>
              </thead>
              <tbody>
                {clubStats.clubs.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-900">{c.full_name}</td>
                    <td className="px-3 py-2">{c.country || "—"}</td>
                    <td className="px-3 py-2 uppercase">{c.language || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
                {clubStats.clubs.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">—</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <div className="tsw-card p-5 space-y-4 max-w-2xl" data-testid="stats-contacts-pane">
          {!isManager ? (
            <>
              <div>
                <label className="tsw-label">{t("statsAdv.consentBasis")}</label>
                <select className="tsw-input" value={contactBasis} onChange={(e) => setContactBasis(e.target.value)} data-testid="stats-contact-basis">
                  <option value="crm">{t("statsAdv.crm")}</option>
                  <option value="commercial">{t("statsAdv.commercial")}</option>
                  <option value="any">{t("statsAdv.any")}</option>
                </select>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{t("statsAdv.contactsNote")}</p>
              <button onClick={downloadContacts} className="tsw-btn-primary" data-testid="stats-contact-download">
                <Download className="w-4 h-4" /> {t("statsAdv.download")}
              </button>
            </>
          ) : (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
              {t("phase2.qaMgrCannotExportContacts")}
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

const Stat = ({ id, label, value }) => (
  <div className="tsw-stat" data-testid={`stat-${id}`}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">{label}</div>
    <div className="font-display font-bold text-2xl text-slate-900">{value ?? "—"}</div>
  </div>
);

const ChartCard = ({ title, data, color, vertical = false, lang }) => (
  <div className="tsw-card p-4">
    <div className="font-semibold text-slate-900 mb-2 text-sm">{title}</div>
    <ResponsiveContainer width="100%" height={vertical ? 240 : 300}>
      {vertical ? (
        <BarChart layout="vertical" data={data} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
          <YAxis dataKey="name" type="category" width={170} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" fill={color} radius={[0, 6, 6, 0]} />
        </BarChart>
      ) : (
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 88, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="name" interval={0} height={100} tick={<StaggeredCategoryTick lang={lang} />} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="count" fill={color} radius={[6, 6, 0, 0]} />
        </BarChart>
      )}
    </ResponsiveContainer>
  </div>
);

export default AdminStatsAdvanced;
